import type { ProgressBarVariantProps } from './ProgressBar.variants';

type Colour = NonNullable<ProgressBarVariantProps['colour']>;
type CircularSize = NonNullable<ProgressBarVariantProps['size']>;

/**
 * Ring geometry per Figma size (read off the exported ring SVGs of frame
 * `2304:9843`): outer box and stroke width in px. The ring's radius is derived
 * in ProgressBar.tsx as `(box - stroke) / 2`; the outer edge sits flush with
 * the box on every size. Shared by circular + semicircle types.
 *
 *   xsmall  → 16px box, 3.2px stroke (circular only)
 *   small   → 24px box, 3.6px stroke (circular only)
 *   default → 32px box, 4.8px stroke
 *   medium  → 56px box, 5.6px stroke
 *   large   → 80px box, 8px stroke
 *   xlarge  → 160px box, 16px stroke (semicircle only)
 */
export const CIRCULAR_GEOMETRY_FOR: Record<
  CircularSize,
  { box: number; stroke: number }
> = {
  xsmall: { box: 16, stroke: 3.2 },
  small: { box: 24, stroke: 3.6 },
  default: { box: 32, stroke: 4.8 },
  medium: { box: 56, stroke: 5.6 },
  large: { box: 80, stroke: 8 },
  xlarge: { box: 160, stroke: 16 },
};

/**
 * Semicircle ("Semi circle" in Figma) is an open-bottom gauge: an arc with a
 * gap centred on 6 o'clock. This is the drawn centreline sweep in degrees; the
 * round stroke caps extend the *painted* arc ~6-10° further each end (more on
 * smaller sizes), so the visible arc reads a touch longer than this value.
 * Tuned to the `2304:9843` ring exports.
 */
export const SEMICIRCLE_SWEEP_DEG = 216;

/**
 * SVG stroke colours for the circular ring. SVG takes `stroke` as a value, not
 * a className, so the CSS-var palette inversion can't reach it — the dark hex
 * is resolved explicitly (same "icons are the exception" rule as Chip/Badge
 * icons). Values from `brand-colors.json`; dark = the mirrored shade
 * (`-50` ↔ `-950`, `-500` stays), matching the Figma dark frame `4890:33310`.
 *
 * Track = `-50` tint (secondary uses `-200` — its `-50` is pure white, see
 * ProgressBar.variants.ts); fill = `-500`, identical in both themes.
 */
const TRACK_COLOR: Record<Colour, { light: string; dark: string }> = {
  // primary-50 / primary-950
  primary: { light: '#f5f3ff', dark: '#2f0d68' },
  // secondary-200 / secondary-800
  secondary: { light: '#e5e5e5', dark: '#262626' },
  // tertiary-50 / tertiary-950
  tertiary: { light: '#eff6ff', dark: '#162456' },
  // green-50 / green-950
  green: { light: '#f0fdf4', dark: '#032e15' },
  // red-50 / red-950
  error: { light: '#fef2f2', dark: '#460809' },
};

const FILL_COLOR: Record<Colour, string> = {
  primary: '#8e51ff', // primary-500
  secondary: '#737373', // secondary-500
  tertiary: '#2b7fff', // tertiary-500
  green: '#00c950', // green-500
  error: '#fb2c36', // red-500
};

export function resolveProgressRingColors(
  colour: Colour,
  isDark: boolean,
): { track: string; fill: string } {
  return {
    track: isDark ? TRACK_COLOR[colour].dark : TRACK_COLOR[colour].light,
    fill: FILL_COLOR[colour],
  };
}
